import React, { useEffect, useMemo, useState } from "react";

import { api, type ProfileMasterItem, type UserProfileItem } from "../lib/api";
import {
  PROFILE_FIELD_DEFINITIONS,
  PROFILE_STEPS,
  getInitialProfileStepIndex,
  hasRequiredProfileValueMap,
  isProfileFieldKey,
  joinProfileListValue,
  splitProfileListValue,
  type ProfileFieldDefinition,
  type ProfileFieldKey,
  type ProfileStepDefinition,
} from "../lib/profileFlow";
import { toProfileSaveErrorMessage } from "../lib/profileSaveError";
import { toUserMessage } from "../lib/errors";
import { UI_TEXT_TONE } from "../lib/uiTone";

type ProfileWizardMode = "onboarding" | "settings";

type ProfileWizardProps = {
  mode: ProfileWizardMode;
  onComplete: () => void;
  onCancel?: () => void;
};

type ProfileValues = Record<ProfileFieldKey, string>;

type MasterLookupItem = {
  label: string;
  displayOrder: number;
};

const PROFILE_FIELD_KEY_SET = new Set<string>(PROFILE_FIELD_DEFINITIONS.map((field) => field.key));

function createEmptyValues(): ProfileValues {
  return PROFILE_FIELD_DEFINITIONS.reduce((acc, field) => {
    acc[field.key] = "";
    return acc;
  }, {} as ProfileValues);
}

function sortProfiles(items: UserProfileItem[]) {
  return [...items].sort((a, b) => a.displayOrder - b.displayOrder || a.label.localeCompare(b.label, "ja"));
}

function toMasterLookup(masterItems: readonly ProfileMasterItem[]): Record<string, MasterLookupItem> {
  return masterItems.reduce<Record<string, MasterLookupItem>>((acc, item) => {
    acc[item.key] = {
      label: item.label,
      displayOrder: item.displayOrder,
    };
    return acc;
  }, {});
}

function toProfileValues(userProfiles: readonly UserProfileItem[]): ProfileValues {
  const nextValues = createEmptyValues();

  userProfiles.forEach((item) => {
    if (isProfileFieldKey(item.key)) {
      nextValues[item.key] = item.value;
    }
  });

  return nextValues;
}

function getPreservedProfiles(userProfiles: readonly UserProfileItem[]): UserProfileItem[] {
  return userProfiles.filter((item) => !PROFILE_FIELD_KEY_SET.has(item.key));
}

function fieldLabel(field: ProfileFieldDefinition, masterLookup: Record<string, MasterLookupItem>): string {
  return masterLookup[field.key]?.label ?? field.label;
}

function fieldDisplayOrder(field: ProfileFieldDefinition, masterLookup: Record<string, MasterLookupItem>): number {
  return masterLookup[field.key]?.displayOrder ?? field.displayOrder;
}

function buildProfilesForSave(
  values: ProfileValues,
  preservedProfiles: readonly UserProfileItem[],
  masterLookup: Record<string, MasterLookupItem>
): UserProfileItem[] {
  const knownProfiles = PROFILE_FIELD_DEFINITIONS.flatMap<UserProfileItem>((field) => {
    const value = values[field.key].trim();
    if (!value) return [];

    return [
      {
        key: field.key,
        label: fieldLabel(field, masterLookup),
        value,
        displayOrder: fieldDisplayOrder(field, masterLookup),
      },
    ];
  });

  return sortProfiles([
    ...preservedProfiles.filter((item) => item.value.trim().length > 0),
    ...knownProfiles,
  ]);
}

function validateStep(step: ProfileStepDefinition, values: ProfileValues): string | null {
  if (step.id === "about" && !values.name.trim()) {
    return "呼ばれ方を入力してください。";
  }

  if (step.id === "communication" && !values.communication_style.trim()) {
    return "話し方の好みを選んでください。";
  }

  return null;
}

function splitKnownAndCustom(value: string, options: readonly string[]) {
  const values = splitProfileListValue(value);
  const optionSet = new Set(options);
  return {
    selected: values.filter((item) => optionSet.has(item)),
    custom: values.filter((item) => !optionSet.has(item)),
  };
}

function FieldBadge({ required, recommended }: { required?: boolean; recommended?: boolean }) {
  if (required) {
    return <span className="rounded-full bg-[#7C5A4E] px-2 py-0.5 text-[10px] font-black text-white">必須</span>;
  }
  if (recommended) {
    return <span className="rounded-full bg-[#FFF3E9] px-2 py-0.5 text-[10px] font-black text-[#A46D4C]">推奨</span>;
  }
  return <span className="rounded-full bg-[#F6EFE9] px-2 py-0.5 text-[10px] font-black text-[#8B7065]">任意</span>;
}

function ChoiceChip({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`min-h-[2.5rem] rounded-full border px-3 py-2 text-sm font-bold transition ${
        selected
          ? "border-[#7C5A4E] bg-[#7C5A4E] text-white shadow-[0_8px_18px_rgba(124,90,78,0.18)]"
          : "border-[#EADBC8] bg-white text-[#5D4D46] hover:bg-[#FFF8F4]"
      }`}
    >
      {label}
    </button>
  );
}

function TextProfileField({
  field,
  value,
  label,
  onChange,
}: {
  field: ProfileFieldDefinition;
  value: string;
  label: string;
  onChange: (value: string) => void;
}) {
  const inputId = `profile-${field.key}`;

  return (
    <div className="rounded-[1.4rem] border border-[#EADBC8] bg-white px-4 py-4">
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor={inputId} className="text-sm font-black text-[#5D4D46]">
          {label}
        </label>
        <FieldBadge required={field.required} recommended={field.recommended} />
      </div>

      {field.options ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {field.options.map((option) => (
            <ChoiceChip key={option} label={option} selected={value.trim() === option} onClick={() => onChange(option)} />
          ))}
        </div>
      ) : null}

      {field.kind === "textarea" ? (
        <textarea
          id={inputId}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="mt-3 min-h-[5.5rem] w-full resize-none rounded-[1rem] border border-[#EADBC8] bg-[#FFFCFA] px-4 py-3 text-sm leading-6 text-[#4A3E39] outline-none transition focus:border-[#D1907B] focus:bg-white"
          placeholder={field.placeholder}
          maxLength={field.maxLength}
        />
      ) : (
        <input
          id={inputId}
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="mt-3 w-full rounded-[1rem] border border-[#EADBC8] bg-[#FFFCFA] px-4 py-3 text-sm text-[#4A3E39] outline-none transition focus:border-[#D1907B] focus:bg-white"
          placeholder={field.placeholder}
          maxLength={field.maxLength}
        />
      )}
      {field.maxLength ? (
        <p className="mt-1 text-right text-[11px] text-[#C4B0A5]">
          {value.length}/{field.maxLength}
        </p>
      ) : null}
    </div>
  );
}

function MultiChoiceProfileField({
  field,
  value,
  label,
  onChange,
}: {
  field: ProfileFieldDefinition;
  value: string;
  label: string;
  onChange: (value: string) => void;
}) {
  const options = field.options ?? [];
  const { selected, custom } = splitKnownAndCustom(value, options);
  const customValue = custom.join("、");
  const inputId = `profile-${field.key}`;

  const updateSelected = (option: string) => {
    const nextSelected = selected.includes(option)
      ? selected.filter((item) => item !== option)
      : [...selected, option];
    onChange(joinProfileListValue([...nextSelected, ...custom]));
  };

  const updateCustom = (nextCustomValue: string) => {
    onChange(joinProfileListValue([...selected, ...splitProfileListValue(nextCustomValue)]));
  };

  return (
    <div className="rounded-[1.4rem] border border-[#EADBC8] bg-white px-4 py-4">
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor={inputId} className="text-sm font-black text-[#5D4D46]">
          {label}
        </label>
        <FieldBadge required={field.required} recommended={field.recommended} />
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {options.map((option) => (
          <ChoiceChip
            key={option}
            label={option}
            selected={selected.includes(option)}
            onClick={() => updateSelected(option)}
          />
        ))}
      </div>
      <input
        id={inputId}
        type="text"
        value={customValue}
        onChange={(event) => updateCustom(event.target.value)}
        className="mt-3 w-full rounded-[1rem] border border-[#EADBC8] bg-[#FFFCFA] px-4 py-3 text-sm text-[#4A3E39] outline-none transition focus:border-[#D1907B] focus:bg-white"
        placeholder={field.placeholder ?? "任意メモ"}
        maxLength={field.maxLength}
      />
      <p className="mt-1 text-xs leading-5 text-[#8B7065]">選んだチップと任意メモをまとめて保存します。</p>
    </div>
  );
}

function SingleChoiceProfileField({
  field,
  value,
  label,
  onChange,
}: {
  field: ProfileFieldDefinition;
  value: string;
  label: string;
  onChange: (value: string) => void;
}) {
  const [showPlayful, setShowPlayful] = useState(false);
  const options = field.options ?? [];
  const playfulOptions = field.playfulOptions ?? [];

  return (
    <div className="rounded-[1.4rem] border border-[#EADBC8] bg-white px-4 py-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-black text-[#5D4D46]">{label}</p>
        <FieldBadge required={field.required} recommended={field.recommended} />
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {options.map((option) => (
          <ChoiceChip key={option} label={option} selected={value.trim() === option} onClick={() => onChange(option)} />
        ))}
      </div>
      <button
        type="button"
        onClick={() => setShowPlayful((prev) => !prev)}
        className="mt-4 rounded-full border border-[#EADBC8] bg-[#FFF8F4] px-3 py-2 text-xs font-black text-[#7C5A4E] transition hover:bg-white"
      >
        {showPlayful ? "遊び心のある話し方を閉じる" : "もっと遊び心のある話し方"}
      </button>
      {showPlayful ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {playfulOptions.map((option) => (
            <ChoiceChip key={option} label={option} selected={value.trim() === option} onClick={() => onChange(option)} />
          ))}
        </div>
      ) : null}
      <p className="mt-3 text-xs leading-5 text-[#8B7065]">
        遊び系は話し方の雰囲気だけを変える設定です。安全性や内容の適切さはそのまま保ちます。
      </p>
    </div>
  );
}

function ProfileField({
  field,
  value,
  label,
  onChange,
}: {
  field: ProfileFieldDefinition;
  value: string;
  label: string;
  onChange: (value: string) => void;
}) {
  if (field.kind === "singleChoice") {
    return <SingleChoiceProfileField field={field} value={value} label={label} onChange={onChange} />;
  }
  if (field.kind === "multiChoice") {
    return <MultiChoiceProfileField field={field} value={value} label={label} onChange={onChange} />;
  }
  return <TextProfileField field={field} value={value} label={label} onChange={onChange} />;
}

export default function ProfileWizard({ mode, onComplete, onCancel }: ProfileWizardProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [values, setValues] = useState<ProfileValues>(() => createEmptyValues());
  const [initialValues, setInitialValues] = useState<ProfileValues>(() => createEmptyValues());
  const [masterLookup, setMasterLookup] = useState<Record<string, MasterLookupItem>>({});
  const [preservedProfiles, setPreservedProfiles] = useState<UserProfileItem[]>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const currentStep = PROFILE_STEPS[currentStepIndex];
  const stepFields = useMemo(
    () => PROFILE_FIELD_DEFINITIONS.filter((field) => field.stepId === currentStep.id),
    [currentStep.id]
  );
  const primaryFields = stepFields.filter((field) => !field.secondary);
  const secondaryFields = stepFields.filter((field) => field.secondary);
  const hasSecondaryFields = secondaryFields.length > 0;
  const stepValidation = validateStep(currentStep, values);
  const isLastStep = currentStepIndex === PROFILE_STEPS.length - 1;
  const canSkipStep = mode === "onboarding" && (currentStep.id === "business" || currentStep.id === "life");

  useEffect(() => {
    let active = true;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [masterItems, userProfiles] = await Promise.all([api.getProfileMaster(), api.getUserProfile()]);
        if (!active) return;

        const nextValues = toProfileValues(userProfiles);
        setValues(nextValues);
        setInitialValues(nextValues);
        setMasterLookup(toMasterLookup(masterItems));
        setPreservedProfiles(getPreservedProfiles(userProfiles));
        setCurrentStepIndex(mode === "onboarding" ? getInitialProfileStepIndex(nextValues) : 0);
      } catch (loadError) {
        if (!active) return;
        setError(toUserMessage(loadError));
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [mode]);

  const updateValue = (key: ProfileFieldKey, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const saveProfiles = async (): Promise<boolean> => {
    const validationMessage = validateStep(currentStep, values);
    if (validationMessage) {
      setError(validationMessage);
      return false;
    }

    setSaving(true);
    setError(null);

    try {
      const profilesForSave = buildProfilesForSave(values, preservedProfiles, masterLookup);
      const savedProfiles = await api.updateUserProfile({
        profiles: profilesForSave.map((item) => ({
          key: item.key,
          value: item.value,
        })),
      });
      const nextValues = toProfileValues(savedProfiles);
      setValues(nextValues);
      setInitialValues(nextValues);
      setPreservedProfiles(getPreservedProfiles(savedProfiles));
      setError(null);
      return true;
    } catch (saveError) {
      setError(toProfileSaveErrorMessage(saveError));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const goNext = async () => {
    const saved = await saveProfiles();
    if (!saved) return;

    if (isLastStep) {
      onComplete();
      return;
    }

    setCurrentStepIndex((prev) => Math.min(prev + 1, PROFILE_STEPS.length - 1));
  };

  const goPrevious = () => {
    setError(null);
    setCurrentStepIndex((prev) => Math.max(prev - 1, 0));
  };

  const skipStep = async () => {
    const saved = await saveProfiles();
    if (!saved) return;

    if (isLastStep) {
      onComplete();
      return;
    }

    setCurrentStepIndex((prev) => Math.min(prev + 1, PROFILE_STEPS.length - 1));
  };

  const moveToStep = (nextStepIndex: number) => {
    if (nextStepIndex === currentStepIndex) return;
    if (mode !== "settings" && nextStepIndex > currentStepIndex) return;

    const validationMessage = validateStep(currentStep, values);
    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    setError(null);
    setCurrentStepIndex(nextStepIndex);
  };

  const handleCancel = () => {
    if (!onCancel) return;

    const isDirty = JSON.stringify(values) !== JSON.stringify(initialValues);
    if (isDirty) {
      const ok = window.confirm("保存されていない変更があります。保存せずに戻りますか？");
      if (!ok) return;
    }
    onCancel();
  };

  const primaryLabel = isLastStep ? (mode === "settings" ? "保存して閉じる" : "保存してはじめる") : "保存して次へ";

  const secondarySummary = mode === "settings" ? "その他の項目" : "任意項目も入力する";

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(244,227,215,0.9),_rgba(253,248,245,1)_55%)] px-4 py-5 text-[#4A3E39] sm:px-6 sm:py-8">
      <div className="mx-auto flex min-h-[calc(100vh-3rem)] max-w-2xl items-center justify-center">
        <section className="relative w-full rounded-[2rem] border border-white/80 bg-white/90 p-4 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-6">
          {onCancel ? (
            <button
              type="button"
              onClick={handleCancel}
              aria-label="閉じる"
              className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#EADBC8] bg-[#FFF8F4] text-[#7C5A4E] transition hover:bg-white"
            >
              ×
            </button>
          ) : null}

          <div className={onCancel ? "pr-12" : ""}>
            <p className={`text-[11px] font-black tracking-[0.16em] ${UI_TEXT_TONE.eyebrow}`}>
              PROFILE {currentStepIndex + 1}/{PROFILE_STEPS.length}
            </p>
            <h1 className={`mt-2 text-2xl font-black tracking-tight ${UI_TEXT_TONE.title}`}>
              {mode === "settings" ? "プロファイル設定" : currentStep.title}
            </h1>
            {mode === "settings" ? (
              <p className={`mt-2 text-sm leading-6 ${UI_TEXT_TONE.body}`}>
                メイがあなたに合わせて話すためのプロファイルです。
              </p>
            ) : (
              <p className={`mt-2 text-sm leading-6 ${UI_TEXT_TONE.body}`}>{currentStep.subtitle}</p>
            )}
          </div>

          <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4" aria-label="プロファイル登録のステップ">
            {PROFILE_STEPS.map((step, index) => {
              const isCurrent = index === currentStepIndex;
              const isAvailable = mode === "settings" || index <= currentStepIndex;

              return (
                <button
                  key={step.id}
                  type="button"
                  onClick={() => moveToStep(index)}
                  disabled={!isAvailable}
                  aria-current={isCurrent ? "step" : undefined}
                  className={`min-h-[3.4rem] rounded-[1rem] border px-3 py-2 text-left transition ${
                    isCurrent
                      ? "border-[#7C5A4E] bg-[#7C5A4E] text-white shadow-[0_10px_22px_rgba(124,90,78,0.18)]"
                      : isAvailable
                        ? "border-[#EADBC8] bg-white text-[#5D4D46] hover:bg-[#FFF8F4]"
                        : "cursor-default border-[#EADBC8] bg-[#F8F0EA] text-[#B9A7A0]"
                  }`}
                >
                  <span className="block text-[10px] font-black uppercase tracking-[0.12em]">
                    STEP {index + 1}
                  </span>
                  <span className="mt-0.5 block text-xs font-black leading-5">{step.navLabel}</span>
                </button>
              );
            })}
          </div>

          <div className="mt-5 rounded-[1.4rem] border border-[#EADBC8] bg-[#FFF9F4] px-4 py-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-lg font-black text-[#7C5A4E]">{currentStep.title}</p>
              <span className="rounded-full bg-white px-3 py-1 text-xs font-black text-[#8B7065]">
                {currentStep.note}
              </span>
            </div>
          </div>

          {loading ? <p className="mt-5 text-sm font-bold text-[#7B6861]">読み込み中です...</p> : null}
          {!loading && error ? (
            <p className="mt-5 rounded-[1rem] bg-[#FFF3F1] px-4 py-3 text-sm font-bold text-[#A1493E]" role="alert">
              {error}
            </p>
          ) : null}

          {!loading ? (
            <div className="mt-5 space-y-4">
              {primaryFields.map((field) => (
                <ProfileField
                  key={field.key}
                  field={field}
                  label={fieldLabel(field, masterLookup)}
                  value={values[field.key]}
                  onChange={(value) => updateValue(field.key, value)}
                />
              ))}

              {hasSecondaryFields ? (
                <details className="rounded-[1.4rem] border border-[#EADBC8] bg-[#FFFDFB] px-4 py-3">
                  <summary className="cursor-pointer text-sm font-black text-[#7C5A4E]">{secondarySummary}</summary>
                  <div className="mt-4 space-y-4">
                    {secondaryFields.map((field) => (
                      <ProfileField
                        key={field.key}
                        field={field}
                        label={fieldLabel(field, masterLookup)}
                        value={values[field.key]}
                        onChange={(value) => updateValue(field.key, value)}
                      />
                    ))}
                  </div>
                </details>
              ) : null}
            </div>
          ) : null}

          {!loading ? (
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              {currentStepIndex > 0 ? (
                <button
                  type="button"
                  onClick={goPrevious}
                  disabled={saving}
                  className="rounded-[1.3rem] border border-[#D9C5B9] bg-white px-4 py-3 text-sm font-black text-[#5D4D46] transition hover:bg-[#FFFCFA] disabled:cursor-not-allowed disabled:opacity-60 sm:w-32"
                >
                  戻る
                </button>
              ) : null}
              {canSkipStep ? (
                <button
                  type="button"
                  onClick={() => void skipStep()}
                  disabled={saving}
                  className="rounded-[1.3rem] border border-[#D9C5B9] bg-white px-4 py-3 text-sm font-black text-[#7C5A4E] transition hover:bg-[#FFFCFA] disabled:cursor-not-allowed disabled:opacity-60 sm:w-40"
                >
                  スキップ
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => void goNext()}
                disabled={Boolean(stepValidation) || saving}
                className="min-h-[3rem] flex-1 rounded-[1.3rem] bg-[#5D4D46] px-4 py-3 text-sm font-black text-[#FDF8F5] shadow-lg transition hover:bg-[#4A3E39] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? "保存中..." : primaryLabel}
              </button>
            </div>
          ) : null}

          {!loading && mode === "onboarding" && !hasRequiredProfileValueMap(values) ? (
            <p className="mt-3 text-xs leading-5 text-[#8B7065]">
              必須は「呼ばれ方」と「話し方の好み」の2項目です。
            </p>
          ) : null}
        </section>
      </div>
    </main>
  );
}
